import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { cn } from "../lib/utils";
import { EmptyState } from "./EmptyState";

/**
 * Lightweight presentational data table.
 * Props:
 *   columns: [{ key, header, render?(row) => node, className?, width? }]
 *   rows: any[]
 *   loading: bool
 *   total, page, pageSize, onPageChange
 *   searchValue, onSearchChange, searchPlaceholder
 *   toolbar: extra filters (right side)
 *   onRowClick(row)
 *   emptyTitle, emptyDescription
 *   testIdPrefix
 */
export function DataTable({
  columns,
  rows = [],
  loading = false,
  total = 0,
  page = 1,
  pageSize = 25,
  onPageChange,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search…",
  toolbar,
  onRowClick,
  emptyTitle = "No data",
  emptyDescription = "Nothing matches your filters yet.",
  testIdPrefix = "table",
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-wrapper`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px] max-w-sm">
          {onSearchChange && (
            <Input
              value={searchValue || ""}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-9 text-sm"
              data-testid={`${testIdPrefix}-search-input`}
            />
          )}
        </div>
        {toolbar && <div className="flex flex-wrap items-center gap-2">{toolbar}</div>}
      </div>

      <div className="border border-border rounded-lg bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`${testIdPrefix}-table`}>
            <thead>
              <tr className="bg-muted/40 border-b border-border">
                {columns.map((c) => (
                  <th
                    key={c.key}
                    style={c.width ? { width: c.width } : undefined}
                    className={cn(
                      "text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wider text-muted-foreground",
                      c.className
                    )}
                  >
                    {c.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`sk-${i}`} className="border-b border-border last:border-0">
                    {columns.map((c) => (
                      <td key={c.key} className="px-4 py-3">
                        <div className="h-3 rounded bg-muted animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              )}
              {!loading && rows.length > 0 && rows.map((row, idx) => (
                <tr
                  key={row.id || idx}
                  onClick={() => onRowClick?.(row)}
                  className={cn(
                    "border-b border-border last:border-0 transition-colors",
                    onRowClick && "cursor-pointer hover:bg-muted/40"
                  )}
                  data-testid={`${testIdPrefix}-row-${row.id || idx}`}
                >
                  {columns.map((c) => (
                    <td key={c.key} className={cn("px-4 py-3 align-middle text-sm", c.className)}>
                      {c.render ? c.render(row) : row[c.key] ?? "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && rows.length === 0 && (
          <div className="p-6">
            <EmptyState title={emptyTitle} description={emptyDescription} testId={`${testIdPrefix}-empty`} />
          </div>
        )}
      </div>

      {onPageChange && total > pageSize && (
        <div className="flex items-center justify-between text-xs text-muted-foreground" data-testid={`${testIdPrefix}-pagination`}>
          <span>
            Showing <span className="font-medium text-foreground">{(page - 1) * pageSize + 1}</span>–
            <span className="font-medium text-foreground">{Math.min(page * pageSize, total)}</span> of{" "}
            <span className="font-medium text-foreground">{total}</span>
          </span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)} data-testid={`${testIdPrefix}-page-prev`}>
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <span className="px-2">Page {page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} data-testid={`${testIdPrefix}-page-next`}>
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
